import React from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box, Button, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';

export function ErrorPage() {
  const { isLoaded: isAuthLoaded, isSignedIn, signOut } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const errorDetail = location.state?.errorDetail
    || 'We could not finish that step. Your information is safe, and you can try again.';

  if (!isAuthLoaded || !isUserLoaded) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#FBF8EF' }}>
        <CircularProgress size={34} />
      </Box>
    );
  }

  const retry = () => navigate(isSignedIn ? '/protected' : '/sign-in', { replace: true });
  const leaveSession = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: '#FBF8EF',
        p: { xs: 2, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', width: 360, height: 360,
          borderRadius: '50%', bgcolor: '#EDF1DF', top: -180, right: -120,
        },
        '&::after': {
          content: '""', position: 'absolute', width: 250, height: 250,
          borderRadius: '50%', bgcolor: '#F3E8D8', bottom: -150, left: -90,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 560,
          border: '1px solid #D6DED2', borderRadius: 4,
          px: { xs: 3, sm: 6 }, py: { xs: 4, sm: 5 }, textAlign: 'center',
          boxShadow: '0 18px 50px rgba(39, 77, 59, 0.09)',
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Box
            aria-hidden="true"
            sx={{
              width: 72, height: 72, display: 'grid', placeItems: 'center',
              borderRadius: '50% 50% 46% 54%', bgcolor: '#EDF1DF',
              color: '#274D3B', fontSize: 36, transform: 'rotate(-4deg)',
            }}
          >
            <Box component="span" sx={{ transform: 'rotate(4deg)' }}>🍃</Box>
          </Box>

          <Box>
            <Typography variant="overline" sx={{ color: '#6F9654', fontWeight: 900, letterSpacing: 1.5 }}>
              MARSTRIX
            </Typography>
            <Typography variant="h4" component="h1" sx={{ color: '#1F352A', fontWeight: 900, mt: 0.5 }}>
              Let’s try that again
            </Typography>
          </Box>

          <Typography sx={{ color: '#647269', fontSize: { xs: 16, sm: 17 }, lineHeight: 1.65, maxWidth: 430 }}>
            {errorDetail}
          </Typography>

          {isSignedIn && user && (
            <Typography variant="body2" sx={{ color: '#647269', bgcolor: '#F7F8F2', borderRadius: 5, px: 2, py: 0.75 }}>
              Signed in as {user.firstName || user.username || user.primaryEmailAddress?.emailAddress || 'user'}
            </Typography>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' }, pt: 0.5 }}>
            <Button
              variant="contained"
              startIcon={isSignedIn ? <RefreshRoundedIcon /> : <ArrowForwardRoundedIcon />}
              onClick={retry}
              sx={{ minWidth: 160, py: 1.15 }}
            >
              {isSignedIn ? 'Try again' : 'Go to sign in'}
            </Button>
            {isSignedIn && (
              <Button
                variant="outlined"
                startIcon={<LogoutRoundedIcon />}
                onClick={leaveSession}
                sx={{ minWidth: 130, py: 1.15 }}
              >
                Sign out
              </Button>
            )}
          </Stack>

          <Typography variant="caption" sx={{ color: '#829087', pt: 0.5 }}>
            If this keeps happening, please try again later or contact Marstrix support.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
